import React, { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { PlusIcon } from 'lucide-react';

import { Button } from '@epoch-ui/components/Button';
import { Checkbox } from '@epoch-ui/components/Checkbox/checkbox';
import { ModalTitle, ModalFooter } from '@epoch-ui/components/Modal/modal';
import { cn } from '@epoch-ui/lib/utils';
import { SessionCard, SessionCardTitleAndDate } from 'components/cards/SessionCard';
import { RsvpSticker } from 'components/cards/components/RsvpSticker/RsvpSticker';
import dayjs from 'utils/extendedDayJS';

import { useGuestSideSheetContext } from '../../guestSideSheetContext';

import { usePlusOneGuestModalContext } from './plusOneGuestModalContext';

export interface SelectGuestFormValues {
  selectedPlusOneIds: number[];
}

export const SelectGuestContent: FC = () => {
  const {
    watch,
    setValue,
    formState: { isDirty }
  } = useFormContext<SelectGuestFormValues>();
  const selectedPlusOneIds = watch('selectedPlusOneIds');

  const { dispatch, savingPlusOneGuest, settingSessionPlusOneGuests } =
    usePlusOneGuestModalContext();

  const {
    guestData: { guest, event },
    allParentPlusOnes,
    selectedSession
  } = useGuestSideSheetContext();
  const eventTimezone = event.timezoneCode || 'UTC';
  const displayedTimezone = dayjs.tz.guess() || eventTimezone;

  const maxAllowedPlusOnes = selectedSession?.plusOneConfig?.maxPlusOnes ?? 0;
  const hasReachedLimit = selectedPlusOneIds.length >= maxAllowedPlusOnes;
  const showNewGuestButton = (guest.plusOnes?.length ?? 0) < maxAllowedPlusOnes && !isDirty;

  return (
    <div className="tw-space-y-5">
      <ModalTitle className="tw-mb-0 tw-text-2xl tw-font-medium">Add guest to session</ModalTitle>

      <div className="tw-flex tw-flex-col tw-gap-3">
        <span className="tw-text-sm tw-font-medium tw-leading-4">
          Add or select guests for this session:
        </span>

        {selectedSession && (
          <SessionCard
            session={selectedSession}
            eventTimezone={eventTimezone}
            displayedTimezone={displayedTimezone}
            className="tw-rounded-xl"
          >
            <SessionCardTitleAndDate />
            <RsvpSticker className="tw-rounded-xl tw-p-2" rsvpStatus={guest.status} />
          </SessionCard>
        )}

        <div className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-border-grey">
          {allParentPlusOnes.map((plusOne, index) => (
            <label
              key={plusOne.plusOneId}
              htmlFor={`select-plusone-${plusOne.plusOneId}`}
              className={cn(
                'tw-m-0 tw-flex tw-cursor-pointer tw-border-0 tw-border-b tw-border-solid tw-border-border-grey tw-p-4',
                !showNewGuestButton && 'last:tw-border-b-0'
              )}
            >
              <div className="tw-flex tw-items-center tw-gap-4 tw-py-1.5">
                <Checkbox
                  id={`select-plusone-${plusOne.plusOneId}`}
                  checked={selectedPlusOneIds.includes(plusOne.plusOneId)}
                  disabled={hasReachedLimit && !selectedPlusOneIds.includes(plusOne.plusOneId)}
                  onCheckedChange={(checked) => {
                    const next = checked
                      ? [...selectedPlusOneIds, plusOne.plusOneId]
                      : selectedPlusOneIds.filter((id) => id !== plusOne.plusOneId);
                    setValue('selectedPlusOneIds', next, { shouldDirty: true });
                  }}
                  className="tw-h-5 tw-w-5"
                />
                <span
                  className={`tw-text-sm tw-font-medium tw-leading-4 ${
                    hasReachedLimit && !selectedPlusOneIds.includes(plusOne.plusOneId)
                      ? 'tw-opacity-40'
                      : ''
                  }`}
                >
                  Guest {index + 1}: {plusOne.firstName} {plusOne.lastName}
                </span>
              </div>
            </label>
          ))}
          {showNewGuestButton && (
            <div className="tw-p-4">
              <Button
                variant="primary"
                size="xxs"
                className="tw-w-fit tw-rounded-xl"
                onClick={() => dispatch({ type: 'GO_TO_STEP', step: 'form' })}
              >
                <PlusIcon size={14} className="tw-mr-1" />
                New guest
              </Button>
            </div>
          )}
        </div>
      </div>

      <ModalFooter className="tw-flex tw-flex-row tw-justify-end">
        <Button
          variant="primary"
          size="sm"
          className="tw-rounded-xl"
          disabled={!isDirty}
          onClick={() => dispatch({ type: 'SUBMIT_SELECT_GUEST', selectedPlusOneIds })}
          loading={savingPlusOneGuest || settingSessionPlusOneGuests}
        >
          Save changes
        </Button>
      </ModalFooter>
    </div>
  );
};
